import { Factory, Award, Users, Layers, Zap, Shield } from 'lucide-react';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_pslcote-labels/artifacts/9jxlmogn_ChatGPT%20Image%20Nov%2017%2C%202025%2C%2010_37_54%20AM.png" 
            alt="PSLCOTE Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/30"></div>
        </div>
        <div className="section-container relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold text-white text-center mb-4 drop-shadow-lg" data-testid="about-page-title">About PSLCOTE</h1>
          <p className="text-lg lg:text-xl text-white text-center mx-auto max-w-3xl drop-shadow-md">
            Part of the Navagraha Group - Leading manufacturers of custom-engineered label stock solutions
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 pslcote-blue" data-testid="brand-story-title">Our Story</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed text-base">
              <p>
                PSLCOTE, a proud member of the Navagraha Group, represents decades of expertise in label stock manufacturing and customer partnership. We have built our reputation on delivering custom-engineered solutions that align seamlessly with our clients' operational workflows and brand identities.
              </p>
              <p>
                Our comprehensive product portfolio spans over 30+ label stock variants, ensuring optimal coverage across diverse applications—from pharmaceutical cold chain logistics to premium wine and spirits labeling, from food and beverage packaging to industrial chemical containers.
              </p>
              <p>
                Through our unique customer partnership model, we collaborate closely with clients using a 3M approach—Material, Manpower, and Machine—to develop highly customized label stock solutions. This holistic methodology ensures not just a product, but a complete, responsive solution that evolves with our clients' demands.
              </p>
              <p>
                With our state-of-the-art manufacturing facility capable of producing 50 lakh square meters monthly, we combine quality assurance with high-speed application capabilities, ensuring superior performance, consistency, and a distinct competitive edge for every customer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Strengths */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="section-container">
          <h2 className="section-title text-center mb-12" data-testid="key-strengths-title">Our Key Strengths</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-backward-integration">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Factory className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Backward Integration</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete in-house manufacturing control ensures consistent quality, delivery reliability, and cost optimization across all product lines.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-custom-engineering">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Layers className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Custom Design & Engineering</h3>
              <p className="text-gray-600 leading-relaxed">
                Tailored solutions based on printing process, application surface, service temperatures, and specific industry requirements.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-quality-delivery">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Shield className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Consistent Quality & Delivery</h3>
              <p className="text-gray-600 leading-relaxed">
                Rigorous quality control processes and reliable delivery schedules ensure your production lines never face interruptions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-technical-capability">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Users className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Service & Technical Capability</h3>
              <p className="text-gray-600 leading-relaxed">
                Expert technical team provides comprehensive support from material selection to application troubleshooting.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-material-choices">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Award className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Wide Material Choices</h3>
              <p className="text-gray-600 leading-relaxed">
                Extensive range including Paper, Film, Foil, PET, BOPP, PE, and Synthetic materials for every application need.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="strength-rapid-response">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Zap className="feature-icon" />
              </div>
              <h3 className="text-xl font-bold mb-3">Rapid Market Response</h3>
              <p className="text-gray-600 leading-relaxed">
                Significant production capacity allows swift scaling to meet urgent demands and accommodate growth opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Capability Highlight */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="bg-gradient-to-br from-[#0066cc] to-[#0052a3] rounded-2xl p-12 text-white text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" data-testid="manufacturing-highlight-title">
              State-of-the-Art Manufacturing Facility
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-3xl mx-auto">
              Our facility houses advanced machinery including Slitter Rewinder, Silicone Coater, and Adhesive Coating machines with hotmelt capabilities up to 1020mm width at 200MPM.
            </p>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-2">Hot Melt</h3>
                <p className="text-sm opacity-90">Advanced adhesive coating</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-2">Water-Based</h3>
                <p className="text-sm opacity-90">Eco-friendly solutions</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-2">Precision Slitting</h3>
                <p className="text-sm opacity-90">Accurate roll processing</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;